import * as Sentry from '@sentry/react';
import type { UseMutateAsyncFunction } from '@tanstack/react-query';
import type {
  AddRetailerRegionToOrganizationMutation,
  AddRetailerRegionToOrganizationMutationVariables,
  AddUserToOrganizationMutation,
  AddUserToOrganizationMutationVariables,
  CreateOrganizationIcpProductCategoryMutation,
  CreateOrganizationIcpProductCategoryMutationVariables,
  CreateOrganizationMutation,
  CreateOrganizationMutationVariables,
} from '@vizit/api';
import { AlertLevel, type ToastData } from '@vizit/atoms';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import type { OrganizationFormSchema } from '@vizit/organisms';

async function createOrganization(
  data: OrganizationFormSchema,
  addToast: (toast: Omit<ToastData, 'id'>) => void,
  createOrganizationMutation: UseMutateAsyncFunction<
    CreateOrganizationMutation,
    unknown,
    CreateOrganizationMutationVariables,
    unknown
  >,
): Promise<CreateOrganizationMutation | undefined> {
  try {
    const response = await createOrganizationMutation({
      name: data.organizationName,
      account_type: data.accountType,
      org_id: undefined,
    });

    if (!response.id) {
      throw new Error('Failed to create organization data record.');
    }

    Sentry.addBreadcrumb({
      message: 'Organization data record created successfully.',
      level: 'info',
      data: { organization_id: response.id },
    });

    return response;
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        feature: SENTRY_FEATURE_TAGS.Organization,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Organization].createOrganizationDataRecord,
      },
      extra: {
        organization_name: data.organizationName,
        account_type: data.accountType,
        l1_product_categories: data.categories,
      },
    });

    addToast({
      title: 'Failed to create organization',
      description: 'Please try again in a few minutes.',
      level: AlertLevel.Error,
    });
  }
}

async function associateIcpProductCategories(
  organizationId: string,
  data: OrganizationFormSchema,
  addToast: (toast: Omit<ToastData, 'id'>) => void,
  createOrganizationIcpProductCategoryMutation: UseMutateAsyncFunction<
    CreateOrganizationIcpProductCategoryMutation,
    unknown,
    CreateOrganizationIcpProductCategoryMutationVariables,
    unknown
  >,
) {
  try {
    Sentry.addBreadcrumb({
      message: 'Associating ICP product categories with organization.',
      level: 'info',
      data: { organization_id: organizationId, categories: data.categories },
    });

    await Promise.all(
      data.categories.map((category) =>
        createOrganizationIcpProductCategoryMutation({
          org_id: organizationId,
          icp_product_category_id: category,
        }),
      ),
    );
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        feature: SENTRY_FEATURE_TAGS.Organization,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Organization].associateIcpProductCategories,
      },
      extra: {
        organization_id: organizationId,
        organization_name: data.organizationName,
        account_type: data.accountType,
        l1_product_categories: data.categories,
      },
    });

    addToast({
      title: 'Failed to associate ICP product categories',
      description: 'Please try to add them manually in a few minutes.',
      level: AlertLevel.Error,
    });
  }
}

async function addUserToOrganization(
  organizationId: string,
  addToast: (toast: Omit<ToastData, 'id'>) => void,
  addUserToOrganizationMutation: UseMutateAsyncFunction<
    AddUserToOrganizationMutation,
    unknown,
    AddUserToOrganizationMutationVariables,
    unknown
  >,
  userEmail?: string | null,
) {
  try {
    Sentry.withScope((scope) => {
      scope.setUser({ user_email: userEmail });

      Sentry.addBreadcrumb({
        message: 'Adding user to organization.',
        level: 'info',
        data: { organization_id: organizationId },
      });
    });

    if (!userEmail) {
      throw new Error('User email is required');
    }

    await addUserToOrganizationMutation({ organization_id: organizationId, email_address: userEmail });
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setUser({ user_email: userEmail });

      Sentry.captureException(error, {
        level: 'error',
        tags: {
          feature: SENTRY_FEATURE_TAGS.Organization,
          stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Organization].addUserToOrganization,
        },
        extra: { organization_id: organizationId },
      });
    });

    addToast({
      title: 'Failed to invite you to the organization',
      description: 'Please reach out to engineering.',
      level: AlertLevel.Error,
    });
  }
}

async function associateRetailerRegions(
  organizationId: string,
  retailerRegionIds: string[],
  addToast: (toast: Omit<ToastData, 'id'>) => void,
  addRetailerRegionToOrganizationMutation: UseMutateAsyncFunction<
    AddRetailerRegionToOrganizationMutation,
    unknown,
    AddRetailerRegionToOrganizationMutationVariables,
    unknown
  >,
) {
  try {
    Sentry.addBreadcrumb({
      message: 'Associating retailer regions with organization.',
      level: 'info',
      data: { organization_id: organizationId, retailer_region_ids: retailerRegionIds },
    });

    await Promise.all(
      retailerRegionIds.map((retailer_region_id) =>
        addRetailerRegionToOrganizationMutation({
          org_id: organizationId,
          retailer_region_id,
        }),
      ),
    );
  } catch (error) {
    Sentry.captureException(error, {
      level: 'error',
      tags: {
        feature: SENTRY_FEATURE_TAGS.Organization,
        stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Organization].associateRetailerRegions,
      },
      extra: {
        organization_id: organizationId,
        retailer_region_ids: retailerRegionIds,
      },
    });

    addToast({
      title: 'Failed to associate retailer regions',
      description: 'Please try to add them manually in a few minutes.',
      level: AlertLevel.Error,
    });
  }
}

export { createOrganization, associateIcpProductCategories, associateRetailerRegions, addUserToOrganization };
